import { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../../../config";
import { speakText } from "../../../utils/audioHelpers";
import LoadingScreen from "../../common/LoadingScreen";
import PronunciationPractice from "../../common/PronunciationPractice";

function VolumeIcon({ size = 20 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
        </svg>
    );
}

function AlphabetPractice({ letters, langTag, lang_id }) {
    const [quizzes, setQuizzes] = useState([]);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [checked, setChecked] = useState(false);
    const [score, setScore] = useState(0);
    const [isFinished, setIsFinished] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [matchingState, setMatchingState] = useState({
        selectedLetter: null,
        selectedSound: null,
        matchedPairs: {} // letter -> sound
    });

    const isArabic = parseInt(lang_id, 10) === 6;

    const loadQuizzes = useCallback(() => {
        setLoading(true);
        setError(null);
        setIsFinished(false);
        setCurrentIdx(0);
        setSelectedOption(null);
        setChecked(false);
        setScore(0);
        setMatchingState({ selectedLetter: null, selectedSound: null, matchedPairs: {} });

        fetch(`${API_BASE_URL}/api/alphabet/quizzes?lang_id=${lang_id}`)
            .then(res => {
                if (!res.ok) throw new Error("Failed to load quizzes.");
                return res.json();
            })
            .then(data => {
                setQuizzes(data.quizzes || []);
            })
            .catch(err => {
                console.error(err);
                setError(err.message || "Could not load practice quizzes.");
            })
            .finally(() => {
                setLoading(false);
            });
    }, [lang_id]);

    useEffect(() => {
        loadQuizzes();
    }, [loadQuizzes]);

    if (loading) {
        return <LoadingScreen inline title="Generating practice questions..." size={50} />;
    }

    if (error) {
        return (
            <div className="alphabet-error-container">
                <p className="alphabet-error-text">{error}</p>
                <button className="alphabet-retry-btn" onClick={loadQuizzes}>Retry</button>
            </div>
        );
    }

    if (quizzes.length === 0) {
        return (
            <div className="alphabet-empty-container">
                <p>No practice items generated. Try adding more letters to the database.</p>
                <button className="alphabet-retry-btn" onClick={loadQuizzes}>Generate</button>
            </div>
        );
    }

    const currentQuiz = quizzes[currentIdx];
    const progress = (currentIdx / quizzes.length) * 100;

    const playSound = (text) => {
        if (!text) return;
        const cleanText = text.trim();
        const found = letters.find(l => l.character === cleanText || l.character_upper === cleanText || l.character_lower === cleanText);
        const textToSpeak = found ? (found.pronunciation || found.character) : text;
        speakText(textToSpeak, langTag);
    };

    const handleOptionSelect = (idx) => {
        if (checked) return;
        setSelectedOption(idx);
    };

    const checkAnswer = () => {
        if (checked) return;

        let correct = false;
        if (currentQuiz.type === 'letter-to-sound' || currentQuiz.type === 'sound-to-letter') {
            if (selectedOption === currentQuiz.correct_index) {
                correct = true;
            }
        } else if (currentQuiz.type === 'matching') {
            const matches = matchingState.matchedPairs;
            const correctPairs = currentQuiz.pairs;
            correct = correctPairs.every(p => matches[p.character] === p.sound);
        }

        if (correct) {
            setScore(prev => prev + 1);
        }
        setChecked(true);
    };

    const nextQuestion = () => {
        setSelectedOption(null);
        setChecked(false);
        setMatchingState({ selectedLetter: null, selectedSound: null, matchedPairs: {} });
        if (currentIdx + 1 < quizzes.length) {
            setCurrentIdx(prev => prev + 1);
        } else {
            setIsFinished(true);
        }
    };

    const handleLetterClick = (letter) => {
        if (checked) return;
        playSound(letter);
        if (matchingState.matchedPairs[letter]) return;

        setMatchingState(prev => {
            const next = { ...prev, selectedLetter: letter };
            if (next.selectedSound) {
                // Form a match
                const updatedPairs = { ...next.matchedPairs, [letter]: next.selectedSound };
                return { selectedLetter: null, selectedSound: null, matchedPairs: updatedPairs };
            }
            return next;
        });
    };

    const handleSoundClick = (sound, actualLetterText) => {
        if (checked) return;
        playSound(actualLetterText);
        // Find if this sound is already matched
        const alreadyMatchedLetter = Object.keys(matchingState.matchedPairs).find(
            k => matchingState.matchedPairs[k] === sound
        );
        if (alreadyMatchedLetter) return;

        setMatchingState(prev => {
            const next = { ...prev, selectedSound: sound };
            if (next.selectedLetter) {
                // Form a match
                const updatedPairs = { ...next.matchedPairs, [next.selectedLetter]: sound };
                return { selectedLetter: null, selectedSound: null, matchedPairs: updatedPairs };
            }
            return next;
        });
    };

    const handlePronunciationResult = (res) => {
        if (res.overall_score >= 60) {
            setScore(prev => prev + 1);
        }
        setChecked(true);
    };

    if (isFinished) {
        return (
            <div className="alphabet-practice-finished">
                <h2>Practice Completed!</h2>
                <div className="alphabet-score-circle">
                    <span className="alphabet-score-num">{score}</span>
                    <span className="alphabet-score-max">/{quizzes.length}</span>
                </div>
                <p className="alphabet-practice-finished__desc">
                    {score === quizzes.length
                        ? "Perfect score! Outstanding work."
                        : score >= quizzes.length / 2
                            ? "Great effort! Keep practicing to get a perfect score."
                            : "Practice makes perfect! Try again."}
                </p>
                <button className="alphabet-practice-finished__btn" onClick={loadQuizzes}>Practice Again</button>
            </div>
        );
    }

    return (
        <div className="alphabet-practice-root" dir={isArabic ? "rtl" : "ltr"}>
            <div className="alphabet-practice-header">
                <div className="alphabet-progressbar-container">
                    <div className="alphabet-progressbar-fill" style={{ width: `${progress}%` }}></div>
                </div>
                <span className="alphabet-question-count">{currentIdx + 1} of {quizzes.length}</span>
            </div>

            <div className="alphabet-question-area">
                <h3 className="alphabet-practice-prompt">{currentQuiz.prompt}</h3>

                {currentQuiz.type === 'letter-to-sound' && (
                    <div className="letter-to-sound-question">
                        <div className="alphabet-question-card">{currentQuiz.character}</div>
                        <button className="alphabet-audio-hint-btn" onClick={() => playSound(currentQuiz.character)}>
                            <VolumeIcon /> Listen
                        </button>
                        <div className="alphabet-options-grid">
                            {currentQuiz.options.map((opt, i) => (
                                <button
                                    key={i}
                                    className={`alphabet-option-btn ${selectedOption === i ? "alphabet-option-btn--selected" : ""}`}
                                    onClick={() => handleOptionSelect(i)}
                                    disabled={checked}
                                >
                                    {opt}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {currentQuiz.type === 'sound-to-letter' && (
                    <div className="sound-to-letter-question">
                        <button className="alphabet-big-audio-btn" onClick={() => playSound(currentQuiz.correct_val || currentQuiz.options[currentQuiz.correct_index])}>
                            <VolumeIcon size={32} />
                            <span>Play Sound</span>
                        </button>
                        <div className="alphabet-options-grid">
                            {currentQuiz.options.map((opt, i) => (
                                <button
                                    key={i}
                                    className={`alphabet-option-btn alphabet-option-btn--large-char ${selectedOption === i ? "alphabet-option-btn--selected" : ""}`}
                                    onClick={() => handleOptionSelect(i)}
                                    disabled={checked}
                                >
                                    {opt}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {currentQuiz.type === 'matching' && (
                    <div className="matching-question">
                        <div className="matching-columns">
                            <div className="matching-col">
                                <h4>Letters</h4>
                                {currentQuiz.pairs.map((p) => {
                                    const isMatched = !!matchingState.matchedPairs[p.character];
                                    const isSelected = matchingState.selectedLetter === p.character;
                                    return (
                                        <button
                                            key={p.character}
                                            className={`matching-item-btn ${isSelected ? "matching-item-btn--selected" : ""} ${isMatched ? "matching-item-btn--matched" : ""}`}
                                            onClick={() => handleLetterClick(p.character)}
                                            disabled={isMatched || checked}
                                        >
                                            {p.character}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="matching-col">
                                <h4>Sounds</h4>
                                {[...currentQuiz.pairs].sort((a, b) => a.sound.localeCompare(b.sound)).map((p) => {
                                    const isMatched = Object.values(matchingState.matchedPairs).includes(p.sound);
                                    const isSelected = matchingState.selectedSound === p.sound;
                                    return (
                                        <button
                                            key={p.sound}
                                            className={`matching-item-btn ${isSelected ? "matching-item-btn--selected" : ""} ${isMatched ? "matching-item-btn--matched" : ""}`}
                                            onClick={() => handleSoundClick(p.sound, p.character)}
                                            disabled={isMatched || checked}
                                        >
                                            {p.sound}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {currentQuiz.type === 'pronunciation' && (
                    <div className="alphabet-pronunciation-question">
                        <div className="pronunciation-hint-card">
                            <div className="pronunciation-hint-char">{currentQuiz.character}</div>
                            <p>Pronounce the example word:</p>
                            <h2 className="pronunciation-target-word">{currentQuiz.text_to_pronounce}</h2>
                            <p className="pronunciation-target-trans">{currentQuiz.translation} (/{currentQuiz.transliteration}/)</p>
                        </div>
                        <PronunciationPractice
                            text={currentQuiz.text_to_pronounce}
                            langCode={langTag}
                            onResult={handlePronunciationResult}
                        />
                    </div>
                )}
            </div>

            {/* Duolingo style footer feedback banner */}
            <div className={`alphabet-action-banner ${checked ? "alphabet-action-banner--checked" : ""}`}>
                {!checked ? (
                    currentQuiz.type !== 'pronunciation' && (
                        <button
                            className="alphabet-verify-btn"
                            disabled={selectedOption === null && currentQuiz.type !== 'matching'}
                            onClick={checkAnswer}
                        >
                            Check
                        </button>
                    )
                ) : (
                    <div className="alphabet-banner-content">
                        {currentQuiz.type === 'pronunciation' ? (
                            <div className="alphabet-banner-msg alphabet-banner-msg--neutral">
                                <span>Pronunciation assessment complete!</span>
                            </div>
                        ) : (
                            selectedOption === currentQuiz.correct_index ||
                            (currentQuiz.type === 'matching' && currentQuiz.pairs.every(p => matchingState.matchedPairs[p.character] === p.sound))
                        ) ? (
                            <div className="alphabet-banner-msg alphabet-banner-msg--correct">
                                <span>Correct! Excellent job.</span>
                            </div>
                        ) : (
                            <div className="alphabet-banner-msg alphabet-banner-msg--incorrect">
                                <span>Incorrect.</span>
                                <span className="correct-answer-text">
                                    {currentQuiz.type === 'letter-to-sound' && `Correct: ${currentQuiz.options[currentQuiz.correct_index]}`}
                                    {currentQuiz.type === 'sound-to-letter' && `Correct character: ${currentQuiz.options[currentQuiz.correct_index]}`}
                                    {currentQuiz.type === 'matching' && `Correct matches: ` + currentQuiz.pairs.map(p => `${p.character} = ${p.sound}`).join(", ")}
                                </span>
                            </div>
                        )}
                        <button className="alphabet-continue-btn" onClick={nextQuestion}>Continue</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AlphabetPractice;
